import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const KEY = "cd-cookie-consent-v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...value, ts: new Date().toISOString() }));
    window.dispatchEvent(new CustomEvent("cd-consent-updated", { detail: value }));
  } catch {}
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [settings, setSettings] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, statistics: false, marketing: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const id = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(id);
    }
  }, []);

  const acceptAll = () => {
    writeConsent({ necessary: true, statistics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, statistics: false, marketing: false });
    setVisible(false);
  };
  const saveSelection = () => {
    writeConsent(prefs);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 md:px-6 md:pb-6 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-[1180px] bg-[color:var(--cream)] border border-black/15 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)] pointer-events-auto">
            <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-7 flex gap-4 items-start">
                <div className="hidden md:flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[color:var(--burgundy)]/30 text-[color:var(--burgundy)]">
                  <Cookie className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--burgundy)]">Datenschutz &amp; Cookies</div>
                  <h2 className="font-serif text-2xl md:text-[26px] leading-tight mt-2">
                    Wir respektieren Ihre <em className="italic">Privatsphäre.</em>
                  </h2>
                  <p className="mt-3 text-[14px] leading-relaxed text-[color:var(--ink-2)] max-w-2xl">
                    Wir setzen ausschließlich technisch notwendige Cookies ein. Für anonyme Statistiken und Marketing bitten wir um Ihre Zustimmung. Sie können Ihre Auswahl jederzeit in unserer{" "}
                    <Link to="/datenschutz" className="link-underline text-[color:var(--ink)]" onClick={() => setVisible(false)}>Datenschutzerklärung</Link> anpassen.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button onClick={rejectAll} data-testid="cookie-reject" className="btn-ghost !py-3 !px-4 !text-[11px] justify-center">Nur notwendige</button>
                  <button onClick={acceptAll} data-testid="cookie-accept" className="btn-primary !py-3 !px-4 !text-[11px] justify-center">Alle akzeptieren</button>
                </div>
                <button
                  onClick={() => setSettings((v) => !v)}
                  data-testid="cookie-settings-toggle"
                  className="text-xs tracking-[0.14em] uppercase text-[color:var(--ink-2)] hover:text-[color:var(--burgundy)] self-start transition-colors"
                >
                  {settings ? "Einstellungen schließen" : "Einstellungen anpassen"}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {settings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="overflow-hidden border-t border-black/10"
                  data-testid="cookie-settings-panel"
                >
                  <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                    <ConsentRow
                      title="Notwendig"
                      desc="Diese Cookies sind für den Betrieb der Website erforderlich (z. B. Sitzung, Kontaktformular)."
                      checked
                      disabled
                    />
                    <ConsentRow
                      title="Statistik"
                      desc="Anonyme Nutzungsstatistiken helfen uns, unsere Website zu verbessern."
                      checked={prefs.statistics}
                      onChange={(v) => setPrefs((p) => ({ ...p, statistics: v }))}
                      testid="cookie-toggle-statistics"
                    />
                    <ConsentRow
                      title="Marketing"
                      desc="Für Reichweitenmessung von Anzeigen — aktuell nicht im Einsatz."
                      checked={prefs.marketing}
                      onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                      testid="cookie-toggle-marketing"
                    />
                  </div>
                  <div className="px-6 md:px-8 pb-6 md:pb-8 flex justify-end">
                    <button onClick={saveSelection} data-testid="cookie-save" className="btn-primary !py-3 !px-5 !text-[11px]">Auswahl speichern</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={rejectAll}
              aria-label="Banner schließen — nur notwendige"
              data-testid="cookie-close"
              className="absolute right-3 top-3 md:right-4 md:top-4 h-7 w-7 flex items-center justify-center text-[color:var(--ink-2)] hover:text-[color:var(--burgundy)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ConsentRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex gap-4 items-start p-4 border ${checked ? "border-[color:var(--burgundy)]/40 bg-[color:var(--burgundy)]/[0.03]" : "border-black/10"} ${disabled ? "opacity-80" : "cursor-pointer hover:border-black/25"} transition-colors`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
        data-testid={testid}
        className={`mt-1 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-[color:var(--burgundy)]" : "bg-black/20"} ${disabled ? "cursor-not-allowed" : ""}`}
      >
        <span className={`absolute top-[2px] h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-[18px]" : "translate-x-[2px]"}`} />
      </button>
      <div>
        <div className="font-serif text-[17px] leading-tight">{title}</div>
        <p className="mt-1 text-[12px] leading-relaxed text-[color:var(--ink-2)]">{desc}</p>
      </div>
    </label>
  );
}
